import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, ScrollText, PanelRightOpen, PanelRightClose } from 'lucide-react';
import { 
  calculatorConfig, 
  isCalculatorAccessible, 
  shouldShowBetaPill,
  shouldShowComingSoonPill,
  shouldShowInSidebar,
  isInBetaEnvironment 
} from '../config/features';
import { trackCalculatorUsage } from '../utils/analytics';

/**
 * SideMenu supports two rendering modes:
 * - Inline (desktop): in-flow sidebar with collapse/expand + log toggle
 * - Drawer (mobile/tablet): full-screen overlay with slide-in animation
 */
const SideMenu = ({
  isDrawer = false,
  isOpen = true,
  onClose,
  collapsed = false,
  onCollapse,
  onExpand,
  logOpen,
  onToggleLog,
  logCount = 0,
  hideLogToggle = false,
}) => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isDrawer && isOpen && onClose) {
        onClose();
      }
    }
  }, [location.pathname, isDrawer, isOpen, onClose]);

  useEffect(() => {
    if (isDrawer && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isDrawer, isOpen]);

  const menuEntries = Object.entries(calculatorConfig).filter(([_, calc]) => {
    if (!isInBetaEnvironment() && calc.hide === true) return false;
    if (!shouldShowInSidebar(calc, location.pathname)) return false;
    return true;
  });

  const renderMenuItem = (calc) => {
    const isActive = location.pathname === calc.path || location.pathname.startsWith(calc.path + '/');
    const isAccessible = isCalculatorAccessible(calc);

    const iconEl = (
      <div className={`flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0 ${
        isActive ? 'bg-conform-black/20 text-conform-black' : 'bg-white/[0.06] text-ink-tertiary'
      }`}>
        {React.cloneElement(calc.icon, { size: 14 })}
      </div>
    );

    if (!isAccessible) {
      return (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg cursor-not-allowed opacity-50 text-ink-tertiary">
          <div className="flex items-center gap-3">
            {iconEl}
            <span className="text-sm">{calc.sideMenu}</span>
          </div>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-conform-yellow/20 text-conform-yellow uppercase tracking-wider">
            Soon
          </span>
        </div>
      );
    }

    return (
      <Link
        to={calc.path}
        onClick={() => trackCalculatorUsage(calc.title, 'navigate', { source: 'sidebar' })}
        className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-150 no-underline ${
          isActive
            ? 'bg-conform-yellow text-conform-black shadow-sm'
            : 'text-ink hover:bg-hover'
        }`}
      >
        <div className="flex items-center gap-3">
          {iconEl}
          <span className="text-sm font-medium">{calc.sideMenu}</span>
        </div>
        <div className="flex gap-1.5 ml-2">
          {shouldShowBetaPill(calc) && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
              isActive ? 'bg-conform-black/20 text-conform-black' : 'bg-conform-red/10 text-conform-red'
            }`}>
              Beta
            </span>
          )}
          {shouldShowComingSoonPill(calc) && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
              isActive ? 'bg-conform-black/20 text-conform-black' : 'bg-conform-yellow/20 text-conform-yellow'
            }`}>
              Soon
            </span>
          )}
        </div>
      </Link>
    );
  };

  const renderCollapsedItem = (calc) => {
    const isActive = location.pathname === calc.path || location.pathname.startsWith(calc.path + '/');
    const isAccessible = isCalculatorAccessible(calc);

    if (!isAccessible) {
      return (
        <div
          className="flex items-center justify-center w-9 h-9 rounded-lg cursor-not-allowed opacity-40 text-ink-tertiary mx-auto"
          title={calc.sideMenu}
        >
          {React.cloneElement(calc.icon, { size: 16 })}
        </div>
      );
    }

    return (
      <Link
        to={calc.path}
        onClick={onExpand}
        className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-150 no-underline mx-auto ${
          isActive
            ? 'bg-conform-yellow text-conform-black shadow-sm'
            : 'text-ink-tertiary hover:bg-hover hover:text-ink'
        }`}
        title={calc.sideMenu}
        aria-label={calc.sideMenu}
      >
        {React.cloneElement(calc.icon, { size: 16 })}
      </Link>
    );
  };

  const renderLogToggle = () => {
    if (hideLogToggle) return null;
    return (
      <button
        onClick={onToggleLog}
        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all duration-150 ${
          logOpen
            ? 'bg-conform-yellow/10 text-conform-yellow'
            : 'text-ink hover:bg-hover'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0 ${
            logOpen ? 'bg-conform-yellow/20 text-conform-yellow' : 'bg-white/[0.06] text-ink-tertiary'
          }`}>
            {logOpen ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
          </div>
          <span className="text-sm font-medium">Log</span>
        </div>
        {logCount > 0 && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            logOpen ? 'bg-conform-yellow/20 text-conform-yellow' : 'bg-conform-yellow/10 text-conform-yellow'
          }`}>
            {logCount}
          </span>
        )}
      </button>
    );
  };

  const renderCollapsedLogToggle = () => {
    if (hideLogToggle) return null;
    return (
      <button
        onClick={onToggleLog}
        className={`relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-150 mx-auto ${
          logOpen
            ? 'bg-conform-yellow/10 text-conform-yellow'
            : 'text-ink-tertiary hover:bg-hover hover:text-ink'
        }`}
        title={logOpen ? 'Hide log panel' : 'Show log panel'}
      >
        {logOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
        {logCount > 0 && !logOpen && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[8px] font-bold rounded-full bg-conform-red-deep text-white leading-none">
            {logCount > 99 ? '99+' : logCount}
          </span>
        )}
      </button>
    );
  };

  // ── INLINE MODE: Desktop sidebar
  if (!isDrawer) {
    if (collapsed) {
      return (
        <div className="relative w-[52px] bg-surface-card rounded-xl border border-border py-2 h-fit flex flex-col items-center group/sidebar">
          <button
            onClick={onExpand}
            className="absolute -right-2.5 top-2 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-surface-card border border-border text-ink-tertiary hover:text-conform-red hover:border-conform-red/30 shadow-sm opacity-0 group-hover/sidebar:opacity-100 transition-all"
            title="Expand sidebar"
          >
            <ChevronRight className="w-3 h-3" />
          </button>

          <nav className="w-full">
            <ul className="space-y-0.5 px-1.5">
              {menuEntries.map(([key, calc]) => (
                <li key={calc.path}>
                  {renderCollapsedItem(calc)}
                </li>
              ))}
            </ul>
          </nav>

          <div className="w-8 border-t border-border-light my-2" />
          {renderCollapsedLogToggle()}
        </div>
      );
    }

    return (
      <div className="relative w-56 bg-surface-card rounded-xl border border-border p-2 h-fit group/sidebar">
        <button
          onClick={onCollapse}
          className="absolute -right-2.5 top-2 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-surface-card border border-border text-ink-tertiary hover:text-conform-red hover:border-conform-red/30 shadow-sm opacity-0 group-hover/sidebar:opacity-100 transition-all"
          title="Collapse sidebar"
        >
          <ChevronLeft className="w-3 h-3" />
        </button>

        <nav>
          <ul className="space-y-0.5">
            {menuEntries.map(([key, calc]) => (
              <li key={calc.path}>
                {renderMenuItem(calc)}
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-border-light my-2" />
        {renderLogToggle()}
      </div>
    );
  }

  // ── DRAWER MODE: Mobile/tablet
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-conform-black shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <Link to="/" onClick={onClose}>
            <img
              src="/images/CT-Wordmark-Red1-sm.png"
              alt="CONFORM.TOOLS"
              width={237}
              height={40}
              className="h-5 w-auto"
            />
          </Link>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-white/50 hover:bg-white/5 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer body */}
        <div className="p-2 overflow-y-auto h-[calc(100%-56px)]">
          <nav>
            <ul className="space-y-0.5">
              {menuEntries.map(([key, calc]) => {
                const isActive = location.pathname === calc.path || location.pathname.startsWith(calc.path + '/');
                const isAccessible = isCalculatorAccessible(calc);

                if (!isAccessible) {
                  return (
                    <li key={calc.path}>
                      <div className="font-headline font-bold text-base px-4 py-3 rounded-lg cursor-not-allowed text-white/30">
                        {calc.sideMenu}
                        <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-conform-yellow/20 text-conform-yellow uppercase tracking-wider">
                          Soon
                        </span>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={calc.path}>
                    <Link
                      to={calc.path}
                      onClick={() => {
                        trackCalculatorUsage(calc.title, 'navigate', { source: 'drawer' });
                        if (onClose) onClose();
                      }}
                      className={`font-headline font-bold text-base px-4 py-3 rounded-lg transition-colors block no-underline ${
                        isActive
                          ? 'text-conform-yellow bg-white/5'
                          : 'text-white hover:text-white/80 hover:bg-white/5'
                      }`}
                    >
                      {calc.sideMenu}
                      {shouldShowBetaPill(calc) && (
                        <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-conform-red/20 text-conform-red uppercase tracking-wider">
                          Beta
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}

              {/* Divider + View Logs link */}
              <li>
                <div className="my-2 border-t border-white/10" />
              </li>
              <li>
                <Link
                  to="/logs"
                  className={`font-headline font-bold text-base px-4 py-3 rounded-lg transition-colors block no-underline ${
                    location.pathname === '/logs'
                      ? 'text-conform-yellow bg-white/5'
                      : 'text-white hover:text-white/80 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <ScrollText size={16} />
                    <span>View Logs</span>
                  </div>
                </Link>
              </li>



            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default SideMenu;
